/**
 * @file assets/index-layout.js
 * @description Distributes the index sections into the left and right columns
 * (right: a section whose grid container, its wrapper's first child, carries
 * data-index-portfolio-kind, which filtered-collection.liquid sets for a
 * catalogue or project collection, and at desktop width the catalogue and
 * projects headings), on mobile promotes the newest right-column card to a
 * featured card and moves the rest into a per-section overflow grid in the
 * left column, hides empty right-column sections and their headings, and
 * places the footer (stating its contentinfo role while the column inside
 * <main> holds it). Moved cards go back to their own places in their grid
 * (L-65). Re-runs when the viewport crosses the 820px edge and, in the theme
 * editor, after a section loads, unloads or is reordered: an unloaded section
 * keeps its slot in the kept section list until its new element loads, and a
 * reordered one takes the place its DOM neighbours give it; every arrangement
 * first replaces a detached section by the element now carrying its id, or
 * drops it. On a product, page, policy or cart route served inside the index shell
 * (body[data-index-shell-panel]) the sections source is empty: at desktop
 * width the script fetches the index, moves its sections and its h1 in,
 * initialises the card behaviours on them and records the index title on the
 * body for dynamic-content.js. Reads window.ThemeDom without a fallback.
 */

(function() {
  'use strict';

  const OVERFLOW_ROOT_ATTRIBUTE = 'data-index-right-column-overflow';
  const OVERFLOW_ROOT_SELECTOR = '[' + OVERFLOW_ROOT_ATTRIBUTE + ']';
  const OVERFLOW_FOR_ATTRIBUTE = 'data-index-right-column-overflow-for';
  /**
   * filtered-collection.liquid puts this attribute, valued `catalogue` or
   * `project`, on the .product-grid-container of a portfolio collection's
   * section, and nothing on any other grid. Only the wrapper's own child is
   * read, so markup inside a card (a product description) cannot move a
   * section (L-22).
   */
  const PORTFOLIO_KIND_ATTRIBUTE = 'data-index-portfolio-kind';
  const RIGHT_COLUMN_CONTAINER_SELECTOR = ':scope > [' + PORTFOLIO_KIND_ATTRIBUTE + ']';
  const RIGHT_COLUMN_GRID_SELECTOR = RIGHT_COLUMN_CONTAINER_SELECTOR + ' > .product-grid';
  /** custom-index-content.liquid marks a catalogue or projects heading with this class on the index. */
  const RIGHT_COLUMN_HEADING_SELECTOR = '.index-collection--right-column-heading';
  /** Shopify's section wrapper: div.shopify-section#shopify-section-<section id>. */
  const SECTION_WRAPPER_CLASS = 'shopify-section';
  const SECTION_WRAPPER_ID_PREFIX = 'shopify-section-';
  /** The one mobile-only class index-page.css styles on a promoted card. */
  const FEATURED_CLASS = 'right-column-featured';
  /**
   * Each right-column card's place in its own grid, recorded the first time an
   * arrangement sees it there (the server's order), so the cards a mobile
   * arrangement moves out go back in that order (L-65).
   */
  const cardPositions = new WeakMap();
  /** The overflow root keeps both collection classes so the card CSS applies to every moved card. */
  const OVERFLOW_ROOT_CLASS = 'index-column-left__right-column-overflow collection-catalogues collection-projects';
  /** product-card.css and index-page.css style the moved cards through .product-grid. */
  const OVERFLOW_GRID_CLASS = 'product-grid';
  /** Body attributes of the index shell (layout/theme.liquid). */
  const SHELL_PANEL_ATTRIBUTE = 'data-index-shell-panel';
  const SHELL_ROOT_ATTRIBUTE = 'data-index-shell-root';
  const SHELL_TITLE_ATTRIBUTE = 'data-index-shell-title';
  /** 'loading', 'loaded' or 'error': dynamic-content.js reads it before closing a served panel. */
  const SHELL_SECTIONS_ATTRIBUTE = 'data-index-shell-sections';
  const SECTIONS_SOURCE_SELECTOR = '.index-sections-source';
  /** The index's visually hidden h1 (layout/theme.liquid); index-page.css decides when it shows. */
  const INDEX_HEADING_SELECTOR = '[data-index-heading]';
  const FETCH_OPTIONS = {
    credentials: 'same-origin',
    headers: { Accept: 'text/html' }
  };

  ThemeDom.onReady(initIndexLayout);

  function initIndexLayout() {
    // Only run inside the index shell (the index and the routes served in its panels).
    if (!document.body.classList.contains('index-shell')) {
      return;
    }

    const sourceContainer = document.querySelector(SECTIONS_SOURCE_SELECTOR);
    const leftColumn = document.querySelector('.index-column-left');
    const rightColumnDefault = document.querySelector('.index-column-right__default');
    const pageFooter = document.querySelector('.page-footer');
    const footerAnchor = pageFooter ? getOrCreateFooterAnchor(pageFooter) : null;

    if (!sourceContainer || !leftColumn || !rightColumnDefault) {
      console.warn('Index layout containers not found');
      return;
    }

    // On the index theme.liquid renders content_for_layout inside .index-sections-source;
    // on a served-panel route the source is empty until the index has been fetched.
    let allSections = Array.from(sourceContainer.children).filter(isIndexSectionElement);
    const isServedPanelRoute = document.body.hasAttribute(SHELL_PANEL_ATTRIBUTE);
    let sectionsRequested = false;
    /**
     * Elements the editor unloaded. Their entries keep their slot in allSections,
     * so a new element with the same id takes exactly that place however many
     * sections were unloaded before it loads (L-65), but no arrangement moves them.
     */
    const unloadedSections = new WeakSet();

    function organizeIndexLayout() {
      refreshSectionReferences();

      // From the kept list, not the columns' children: an element the editor is
      // about to remove may still sit in a column.
      const sections = allSections.filter(section => !unloadedSections.has(section));

      if (!sections.length) {
        placeIndexFooter(leftColumn, pageFooter, footerAnchor);
        return;
      }

      mountSections(sections, leftColumn, rightColumnDefault);
      restoreMobileRightColumnOverflow(leftColumn, rightColumnDefault);

      const overflowRoot = organizeMobileRightColumnFeature(leftColumn, sections.filter(isRightColumnSection));

      updateRightColumnSectionVisibility(sections, overflowRoot);
      placeIndexFooter(leftColumn, pageFooter, footerAnchor);
    }

    /**
     * Swap each detached entry of allSections for the element that now carries
     * its id, or drop it when none does, so an arrangement never moves a
     * removed element back into a column (L-63). The cards a detached element
     * left in the mobile overflow grid go with it; a replacement brings its own.
     */
    function refreshSectionReferences() {
      allSections = allSections.reduce((sections, section) => {
        let current = section;

        if (!section.isConnected) {
          discardSectionOverflow(leftColumn, section.id);
          current = document.getElementById(section.id);
        }

        if (current && !sections.includes(current)) {
          sections.push(current);
        }

        return sections;
      }, []);
    }

    /** One arrangement after a burst of editor events, once the editor has swapped the elements. */
    const scheduleIndexLayout = ThemeDom.debounce(organizeIndexLayout, 0);

    /**
     * The theme editor re-renders a section by firing shopify:section:unload on
     * its element (still in place), replacing the element and firing
     * shopify:section:load on the new one; it fires load alone for a section
     * it adds and unload alone for one it removes. An unloaded entry stays in
     * allSections, out of the arrangement, and a loaded element takes the place
     * of the entry with its id, so several sections unloaded before any loads
     * each come back to their own slot; a removed section's entry is dropped
     * once its element is gone. An added section goes in by its neighbours
     * (insertSectionByNeighbour), which keeps the place the editor inserted it
     * at. Events for sections outside the columns and the source (the header,
     * the footer, a served panel's own section) are ignored.
     */
    function handleEditorSectionLoad(event) {
      const section = getEditorEventSection(event);

      if (!section || !isIndexSectionElement(section)) {
        return;
      }

      const index = findSectionIndex(allSections, section.id);

      if (index !== -1) {
        if (allSections[index] === section && !unloadedSections.has(section)) {
          return;
        }

        allSections[index] = section;
        unloadedSections.delete(section);
      } else if (isSectionContainer(section.parentNode)) {
        insertSectionByNeighbour(section);
      } else {
        return;
      }

      // Overflow cards under this id came from the element the new one replaces.
      discardSectionOverflow(leftColumn, section.id);
      scheduleIndexLayout();
    }

    function handleEditorSectionUnload(event) {
      const section = getEditorEventSection(event);
      const index = section ? findSectionIndex(allSections, section.id) : -1;

      if (index === -1) {
        return;
      }

      allSections[index] = section;
      unloadedSections.add(section);
      discardSectionOverflow(leftColumn, section.id);
      scheduleIndexLayout();
    }

    /**
     * The editor moves a reordered section's element and then fires
     * shopify:section:reorder on it (detail: { sectionId }). The kept order
     * would put it back at the next arrangement, so its entry is taken out and
     * put back by its new DOM neighbours (L-65), on the assumption that the
     * editor places the element beside a section it now follows or precedes.
     */
    function handleEditorSectionReorder(event) {
      const section = getEditorEventSection(event);
      const index = section ? findSectionIndex(allSections, section.id) : -1;

      if (index === -1 || !isSectionContainer(section.parentNode)) {
        return;
      }

      allSections.splice(index, 1);
      insertSectionByNeighbour(section);
      scheduleIndexLayout();
    }

    function isSectionContainer(node) {
      return node === sourceContainer || node === leftColumn || node === rightColumnDefault;
    }

    /**
     * Put a section into allSections after the nearest known section before its
     * element in the DOM, else before the nearest one after it, else last.
     */
    function insertSectionByNeighbour(section) {
      for (let sibling = section.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
        const index = findSectionIndex(allSections, sibling.id);

        if (index !== -1) {
          allSections.splice(index + 1, 0, section);
          return;
        }
      }

      for (let sibling = section.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
        const index = findSectionIndex(allSections, sibling.id);

        if (index !== -1) {
          allSections.splice(index, 0, section);
          return;
        }
      }

      allSections.push(section);
    }

    /**
     * Fill the columns of a served-panel route with the index's sections, and put
     * the index's h1 in front of them so the index has its heading once the
     * served panel closes (the served markup carries only the page's own h1).
     * Only at desktop width, where the columns show; a page loaded narrow fetches
     * when it is first widened. One request per page; a failure leaves the left
     * column empty (the served page stays usable) and lets a later widening retry.
     */
    function requestIndexSections() {
      if (!isServedPanelRoute || allSections.length || sectionsRequested || !ThemeDom.isDesktopViewport()) {
        return;
      }

      sectionsRequested = true;
      document.body.setAttribute(SHELL_SECTIONS_ATTRIBUTE, 'loading');

      fetchIndexSections(document.body.getAttribute(SHELL_ROOT_ATTRIBUTE) || ThemeDom.storeUrl(''))
        .then(({ sections, title, heading }) => {
          sections.forEach(section => {
            sourceContainer.appendChild(document.adoptNode(section));
          });

          if (heading && !document.querySelector(INDEX_HEADING_SELECTOR)) {
            leftColumn.parentNode.insertBefore(document.adoptNode(heading), leftColumn);
          }

          allSections = Array.from(sourceContainer.children).filter(isIndexSectionElement);

          if (title) {
            document.body.setAttribute(SHELL_TITLE_ATTRIBUTE, title);
          }

          organizeIndexLayout();
          initializeFetchedSections(allSections);
          document.body.setAttribute(SHELL_SECTIONS_ATTRIBUTE, 'loaded');
        })
        .catch(error => {
          sectionsRequested = false;
          document.body.setAttribute(SHELL_SECTIONS_ATTRIBUTE, 'error');
          console.warn('Index shell: could not load the index sections', error);
        });
    }

    organizeIndexLayout();
    requestIndexSections();

    // Every placement decision depends on the desktop/mobile edge alone.
    ThemeDom.onViewportEdgeChange(() => {
      requestIndexSections();
      organizeIndexLayout();
    }, 250);

    // Fired only inside the theme editor.
    document.addEventListener('shopify:section:load', handleEditorSectionLoad);
    document.addEventListener('shopify:section:unload', handleEditorSectionUnload);
    document.addEventListener('shopify:section:reorder', handleEditorSectionReorder);
  }

  /**
   * The section wrapper an editor event names: its target, else the element
   * with the id the wrapper of `detail.sectionId` carries.
   */
  function getEditorEventSection(event) {
    if (event.target instanceof Element && event.target.classList.contains(SECTION_WRAPPER_CLASS)) {
      return event.target;
    }

    const sectionId = event.detail && event.detail.sectionId;

    return sectionId ? document.getElementById(SECTION_WRAPPER_ID_PREFIX + sectionId) : null;
  }

  function findSectionIndex(sections, id) {
    return id ? sections.findIndex(section => section.id === id) : -1;
  }

  /**
   * The index's section elements, h1 and document title: `{ sections, heading,
   * title }` from a same-origin fetch of `url` (`heading` is null if the page
   * has none), or a rejection when the page has no sections source (a redirect
   * elsewhere, an error page).
   */
  function fetchIndexSections(url) {
    return fetch(url, FETCH_OPTIONS)
      .then(response => {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }

        return response.text();
      })
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector(SECTIONS_SOURCE_SELECTOR);
        const sections = source ? Array.from(source.children).filter(isIndexSectionElement) : [];

        if (!sections.length) {
          throw new Error('No index sections in the fetched page');
        }

        // The served page's own main image holds the one high-priority fetch; the
        // index's cover picture arrives later and must not compete with it. A
        // parsed document loads nothing, so the hint goes before any request.
        source.querySelectorAll('img[fetchpriority="high"]').forEach(image => {
          image.removeAttribute('fetchpriority');
        });

        return {
          sections,
          heading: doc.querySelector(INDEX_HEADING_SELECTOR),
          title: ThemeDom.normalizeWhitespace(doc.title)
        };
      });
  }

  /**
   * The behaviours the card script gives the index's sections at load, run on
   * the sections fetched after it (the script is loaded on every shell route,
   * read at call time and skipped when absent). Pictures load natively, and
   * the <media-carousel> elements set themselves up when inserted.
   */
  function initializeFetchedSections(sections) {
    sections.forEach(section => {
      if (window.ProductCard && typeof window.ProductCard.initialize === 'function') {
        window.ProductCard.initialize(section);
      }
    });
  }

  function mountSections(allSections, leftColumn, rightColumnDefault) {
    const isDesktop = ThemeDom.isDesktopViewport();
    const leftSections = [];
    const rightSections = [];

    allSections.forEach(section => {
      if (isRightColumnSection(section) || (isDesktop && isRightColumnHeadingSection(section))) {
        rightSections.push(section);
      } else {
        leftSections.push(section);
      }
    });

    placeInOrder(leftColumn, leftSections);
    placeInOrder(rightColumnDefault, rightSections);
  }

  /**
   * Put elements (a column's sections, a grid's cards) at the start of a
   * container in the given order, moving only the ones whose parent or position
   * differs from the target so an unchanged layout does not re-append (and
   * re-render) every one.
   */
  function placeInOrder(container, elements) {
    let previousElement = null;

    elements.forEach(element => {
      const isPlaced = element.parentNode === container && (
        previousElement
          ? element.previousElementSibling === previousElement
          : element === container.firstElementChild
      );

      if (!isPlaced) {
        container.insertBefore(element, previousElement ? previousElement.nextSibling : container.firstChild);
      }

      previousElement = element;
    });
  }

  /**
   * On mobile, promote the newest right-column card to a featured card and move
   * the rest into a per-section overflow grid inside the left column. Takes the
   * right-column sections in column order. Returns the overflow root when one
   * was filled, otherwise null.
   */
  function organizeMobileRightColumnFeature(leftColumn, rightColumnSections) {
    const rightColumnItems = [];

    rightColumnSections.forEach((section, sectionIndex) => {
      const productGrid = getRightColumnProductGrid(section);
      if (!productGrid) return;

      // The grid's own cards only, as the visibility count reads them. Every card
      // is in its grid here (the overflow was restored first), so a card seen for
      // the first time records its server position.
      getDirectGridItems(productGrid).forEach((item, itemIndex) => {
        if (!cardPositions.has(item)) {
          cardPositions.set(item, itemIndex);
        }

        item.classList.remove(FEATURED_CLASS);

        rightColumnItems.push({
          item,
          section,
          sectionIndex,
          itemIndex,
          timestamp: getCardTimestamp(item)
        });
      });
    });

    if (ThemeDom.isDesktopViewport() || !rightColumnItems.length) {
      return null;
    }

    const featuredEntry = getNewestRightColumnEntry(rightColumnItems);
    let overflowRoot = null;

    rightColumnItems.forEach(entry => {
      if (entry === featuredEntry) {
        entry.item.classList.add(FEATURED_CLASS);
        return;
      }

      if (!overflowRoot) {
        overflowRoot = leftColumn.querySelector(OVERFLOW_ROOT_SELECTOR) || createMobileRightColumnOverflow(leftColumn);
      }

      let sectionOverflow = getSectionOverflow(overflowRoot, entry.section.id);

      if (!sectionOverflow) {
        sectionOverflow = document.createElement('div');
        sectionOverflow.className = OVERFLOW_GRID_CLASS;
        sectionOverflow.setAttribute(OVERFLOW_FOR_ATTRIBUTE, entry.section.id);
        overflowRoot.appendChild(sectionOverflow);
      }

      sectionOverflow.appendChild(entry.item);
    });

    return overflowRoot;
  }

  /**
   * Move overflow cards back into their section grids before the layout is
   * recomputed, each grid in its recorded card order: the promoted card stayed
   * in its grid, so appending the others after it would put it first (L-65).
   */
  function restoreMobileRightColumnOverflow(leftColumn, rightColumnDefault) {
    const overflowRoot = leftColumn.querySelector(OVERFLOW_ROOT_SELECTOR);

    if (!overflowRoot) return;

    Array.from(overflowRoot.children).forEach(sectionOverflow => {
      const targetSection = document.getElementById(sectionOverflow.getAttribute(OVERFLOW_FOR_ATTRIBUTE) || '');
      const isRightColumnTarget = targetSection
        && targetSection.parentNode === rightColumnDefault
        && isRightColumnSection(targetSection);
      const targetGrid = isRightColumnTarget ? getRightColumnProductGrid(targetSection) : null;

      if (!targetGrid) return;

      Array.from(sectionOverflow.children).forEach(item => {
        item.classList.remove(FEATURED_CLASS);
        targetGrid.appendChild(item);
      });

      sectionOverflow.remove();
      placeInOrder(targetGrid, getDirectGridItems(targetGrid).sort((a, b) => getCardPosition(a) - getCardPosition(b)));
    });

    if (overflowRoot.children.length === 0) {
      overflowRoot.remove();
    }
  }

  function createMobileRightColumnOverflow(leftColumn) {
    const overflowRoot = document.createElement('div');

    overflowRoot.className = OVERFLOW_ROOT_CLASS;
    overflowRoot.setAttribute(OVERFLOW_ROOT_ATTRIBUTE, '');
    leftColumn.appendChild(overflowRoot);

    return overflowRoot;
  }

  function getOrCreateFooterAnchor(pageFooter) {
    const existingAnchor = document.querySelector('[data-index-footer-anchor]');

    if (existingAnchor) {
      return existingAnchor;
    }

    const anchor = document.createElement('div');
    anchor.setAttribute('data-index-footer-anchor', '');
    pageFooter.parentNode.insertBefore(anchor, pageFooter);

    return anchor;
  }

  function placeIndexFooter(leftColumn, pageFooter, footerAnchor) {
    if (!leftColumn || !pageFooter || !footerAnchor) {
      return;
    }

    if (!ThemeDom.isDesktopViewport()) {
      if (pageFooter.parentNode !== footerAnchor.parentNode || pageFooter.previousElementSibling !== footerAnchor) {
        footerAnchor.insertAdjacentElement('afterend', pageFooter);
      }

      pageFooter.classList.remove('page-footer--index-column');
      setFooterLandmark(pageFooter, false);
      return;
    }

    if (pageFooter.parentNode !== leftColumn || pageFooter !== leftColumn.lastElementChild) {
      leftColumn.appendChild(pageFooter);
    }

    pageFooter.classList.add('page-footer--index-column');
    setFooterLandmark(pageFooter, true);
  }

  /**
   * A <footer> inside <main> is not the page's contentinfo landmark, so while
   * the left column (inside main) holds it the role is stated; back outside
   * main the element carries it natively and the attribute goes (D-049).
   */
  function setFooterLandmark(pageFooter, insideMain) {
    const footer = pageFooter.querySelector('footer');

    if (!footer) {
      return;
    }

    if (insideMain) {
      footer.setAttribute('role', 'contentinfo');
    } else {
      footer.removeAttribute('role');
    }
  }

  function updateRightColumnSectionVisibility(allSections, overflowRoot) {
    const isDesktop = ThemeDom.isDesktopViewport();
    const sectionCountsByType = new Map();

    allSections.filter(isRightColumnSection).forEach(section => {
      const sectionType = getRightColumnSectionType(section);
      const productGrid = getRightColumnProductGrid(section);
      const sectionItems = productGrid ? getDirectGridItems(productGrid) : [];
      const overflowItems = getMobileOverflowItemsForSection(overflowRoot, section);
      const totalItemCount = sectionItems.length + overflowItems.length;
      const regularItemCount = isDesktop ? totalItemCount : overflowItems.length;
      const existingCounts = sectionCountsByType.get(sectionType) || {
        totalItemCount: 0,
        regularItemCount: 0
      };

      existingCounts.totalItemCount += totalItemCount;
      existingCounts.regularItemCount += regularItemCount;
      sectionCountsByType.set(sectionType, existingCounts);

      ThemeDom.setElementHidden(section, totalItemCount === 0 || (!isDesktop && sectionItems.length === 0 && overflowItems.length > 0));
    });

    allSections
      .filter(isRightColumnHeadingSection)
      .forEach(section => {
        const counts = sectionCountsByType.get(getRightColumnHeadingSectionType(section)) || {
          totalItemCount: 0,
          regularItemCount: 0
        };
        const shouldHideHeading = counts.totalItemCount === 0 || counts.regularItemCount === 0;

        ThemeDom.setElementHidden(section, shouldHideHeading);
      });
  }

  function getDirectGridItems(grid) {
    return Array.from(grid.children).filter(child => child.classList.contains('product-grid-item'));
  }

  /** The overflow grid holding the moved cards of the section with this id, or null. */
  function getSectionOverflow(overflowRoot, sectionId) {
    return overflowRoot.querySelector('[' + OVERFLOW_FOR_ATTRIBUTE + '="' + sectionId + '"]');
  }

  function getMobileOverflowItemsForSection(overflowRoot, section) {
    const sectionOverflow = overflowRoot ? getSectionOverflow(overflowRoot, section.id) : null;

    return sectionOverflow ? getDirectGridItems(sectionOverflow) : [];
  }

  /**
   * Remove the overflow grid of a section whose element is gone or replaced:
   * its cards belong to that element. The next arrangement drops an emptied
   * overflow root.
   */
  function discardSectionOverflow(leftColumn, sectionId) {
    const overflowRoot = leftColumn.querySelector(OVERFLOW_ROOT_SELECTOR);
    const sectionOverflow = overflowRoot ? getSectionOverflow(overflowRoot, sectionId) : null;

    if (sectionOverflow) {
      sectionOverflow.remove();
    }
  }

  /** The section's own .product-grid, under its marked grid container; null for a left-column section. */
  function getRightColumnProductGrid(section) {
    return section.querySelector(RIGHT_COLUMN_GRID_SELECTOR);
  }

  /**
   * A section belongs in the right column exactly when its grid container
   * carries the portfolio kind; headings are matched by their own markers.
   */
  function isRightColumnSection(section) {
    return section.querySelector(RIGHT_COLUMN_CONTAINER_SELECTOR) !== null;
  }

  function isRightColumnHeadingSection(section) {
    return section.querySelector(RIGHT_COLUMN_HEADING_SELECTOR) !== null;
  }

  /** Only called for heading sections, which are either a projects or a catalogue heading. */
  function getRightColumnHeadingSectionType(section) {
    return section.querySelector('.index-collection--projects-heading') ? 'project' : 'catalogue';
  }

  /** `catalogue` or `project`, the values getRightColumnHeadingSectionType returns. Only called for right-column sections. */
  function getRightColumnSectionType(section) {
    return section.querySelector(RIGHT_COLUMN_CONTAINER_SELECTOR).getAttribute(PORTFOLIO_KIND_ATTRIBUTE);
  }

  /** A card's recorded place in its grid; an unrecorded card sorts last, keeping its order. */
  function getCardPosition(item) {
    return cardPositions.has(item) ? cardPositions.get(item) : Number.MAX_SAFE_INTEGER;
  }

  function getCardTimestamp(item) {
    const timestamp = Number(item.getAttribute('data-index-card-date'));

    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  /**
   * The entry with the highest timestamp. Entries arrive in section and item
   * order, so keeping the first of equal timestamps is the tie-break.
   */
  function getNewestRightColumnEntry(entries) {
    return entries.reduce(
      (newest, entry) => (!newest || entry.timestamp > newest.timestamp ? entry : newest),
      null
    );
  }

  function isIndexSectionElement(section) {
    return section && section.nodeType === Node.ELEMENT_NODE && section.id;
  }
})();
